import { useEffect, useState, useContext } from "react";
// @mui
import { Alert, Card, Stack } from "@mui/material";

import DarkWebMonitoringForm from "./DarkWebMonitoringForm";
import BreachTimeline from "./BreachTimeline";
import axios from "../../../../../utils/axios";
import useBoolean from "../../../../../hooks/useBoolean.ts";
import FeatureDescriptionAccordion from "./FeatureDescriptionAccordion";
import BreachAlertTips from "./BreachAlertTips";
import { AuthContext } from "../../../../../auth/JwtContext";
import { useSnackbar } from "../../../../../components/snackbar";
import { useLocales } from "../../../../../locales";

export default function AccountSettings(props) {
  const { user, updateLocalUser } = useContext(AuthContext);
  const { translate } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const drawer = useBoolean();
  const [breaches, setBreaches] = useState([]);

  const isMonitoringEnabled = user?.darkWebMonitoringEnabled || false;
  const isScanned = user?.darkWebMonitoringScanned || false;

  const handleToggleMonitoring = async (formData) => {
    try {
      const response = await axios.post("user/switch-email-leak-monitoring");

      enqueueSnackbar(translate(`snackBarUpdateSuccess`), {
        variant: "success",
      });

      updateLocalUser(response.data);

      if (!formData.darkWebMonitoringEnabled) {
        setBreaches([]);
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || translate(`snackBarUpdateError`), {
        variant: "error",
      });
      throw error;
    }
  };

  useEffect(() => {
    let intervalId;

    const pollUserProfile = async () => {
      try {
        const response = await axios.get("/user/profile");

        updateLocalUser(response.data);
      } catch (error) {
        console.error("Error:", error);
      }
    };

    if (isMonitoringEnabled && !isScanned) {
      intervalId = setInterval(pollUserProfile, 5000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isMonitoringEnabled, isScanned, updateLocalUser]);

  useEffect(() => {
    const fetchLeaks = async () => {
      try {
        const response = await axios.get("/user-email-leak");
        if (Array.isArray(response.data)) {
          setBreaches(response.data);
        }
      } catch (error) {
        console.error("Error:", error);
      }
    };

    if (isMonitoringEnabled && isScanned) {
      fetchLeaks();
    } else if (!isMonitoringEnabled) {
      setBreaches([]);
    }
  }, [isMonitoringEnabled, isScanned]);

  let scanStatus = "idle";
  if (isMonitoringEnabled) {
    if (!isScanned) {
      scanStatus = "polling";
    } else {
      scanStatus = "completed";
    }
  }

  return (
    <Stack>
      {!drawer.value && (
        <Alert
          severity={isMonitoringEnabled ? "success" : "warning"}
          onClose={drawer.onTrue}
          sx={{ mb: 2 }}
        >
          {isMonitoringEnabled
            ? translate("monitoringPage.featureActivatedAlert")
            : translate("monitoringPage.featureNotActivatedAlert")}
        </Alert>
      )}

      <Card sx={{ p: 3, mb: 2 }}>
        <DarkWebMonitoringForm
          user={user}
          scanStatus={scanStatus}
          onSubmitForm={handleToggleMonitoring}
        />

        {isMonitoringEnabled && isScanned && breaches.length > 0 && (
          <>
            <BreachAlertTips breaches={breaches} />
            <BreachTimeline breaches={breaches} />
          </>
        )}
      </Card>
      <FeatureDescriptionAccordion />
    </Stack>
  );
}
